"use client";
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { HoverCard, HoverCardProps } from "@ui/components/hover-card";
import { FAQSection, Faqs_T } from "@ui/marketing/faq/faq";
import { SectionTemplate } from "components/individuals/blocks";
import Image from "next/image";
import { customRichText } from "./individuals/mdx-components";

export interface FeatureCardsSectionProps {
  title: string;
  subtitle: string;
  titleColor: string;
  subtitleColor: string;
  bg: string;
  infoCards: HoverCardProps[];
  faqSection: Omit<Faqs_T, "renderAnswer">;
  /**
   * Indicates if the data is for b2b or b2c
   */
  from?: "enterprise" | "b2c";
}

export const FeatureCardsSection = ({
  title,
  subtitle,
  titleColor,
  subtitleColor,
  bg,
  infoCards,
  faqSection,
  from,
}: FeatureCardsSectionProps) => {
  const [isLargeBp] = useMediaQuery("(min-width: 992px)");
  return (
    <SectionTemplate bg={bg}>
      <Heading
        as={"h2"}
        color={titleColor}
        fontSize={{
          base: 26,
          md: 34,
          lg: 38,
          xl: 45,
        }}
        lineHeight={1.2}
        fontWeight={"bold"}
        mb={{
          base: from === "b2c" ? 2 : 4,
          lg: from === "b2c" ? 4 : 6,
        }}
        textAlign={from === "b2c" ? "center" : "left"}
      >
        {title}
      </Heading>
      <Text
        as={"h3"}
        color={subtitleColor}
        fontSize={
          from === "b2c"
            ? {
                base: 14,
                md: 16,
                lg: 17,
                xl: 18,
              }
            : {
                base: 16,
                md: 18,
                lg: 20,
                xl: 22,
              }
        }
        fontWeight={from === "b2c" ? "" : "bold"}
        mb={{
          base: 0,
          lg: 14,
        }}
        textAlign={from === "b2c" ? "center" : "left"}
      >
        {subtitle}
      </Text>
      {isLargeBp ? (
        <Grid templateColumns={"repeat(3, 1fr)"} columnGap={12} rowGap={8}>
          {infoCards.map((card, idx) => (
            <GridItem key={idx}>
              {card.icon && (
                <Box w={"120px"} mx={"auto"} mb={5}>
                  <Image
                    src={card.icon.url}
                    width={120}
                    height={58}
                    alt={card.icon?.alt}
                    placeholder={
                      typeof card.icon?.blurDataURL === "string"
                        ? "blur"
                        : undefined
                    }
                    blurDataURL={
                      typeof card.icon?.blurDataURL === "string"
                        ? card.icon?.blurDataURL
                        : undefined
                    }
                  />
                </Box>
              )}
              <HoverCard {...card} />
            </GridItem>
          ))}
        </Grid>
      ) : (
        <Box>
          <FAQSection
            renderAnswer={(answer) => (
              <RichText content={answer?.raw} renderers={customRichText()} />
            )}
            from={from}
            {...faqSection}
          />
        </Box>
      )}
    </SectionTemplate>
  );
};
